/**
 * Helpers for fixed text boxes.
 *
 * A text var's size_y is normally whatever height the editor measured for its
 * text when the template was saved, and text that renders longer bleeds over
 * whatever sits below. A fixed box keeps the height the designer dragged it
 * to instead, and can shrink its text to fit:
 *
 *   "fixed_height": true,
 *   "shrink_to_box": true,
 *   "box_min_font_size": 25
 *
 *   fixed_height      - size_y is the designer's box height, not the measured
 *                       text height
 *   shrink_to_box     - requires fixed_height - when the rendered text is
 *                       taller than the box, the stitcher lowers its font size
 *                       to the largest size that fits; text still too tall at
 *                       the floor stays at the floor and runs past the bottom
 *                       of the box (in a stack, pushing the members below)
 *   box_min_font_size - px floor for that, never below the system floor
 *
 * The keys sit at the top level of the metadata, outside `stack`: they belong
 * to the element, not to a stack. In a stack a fixed box is the member's
 * designed height, and with shrink_to_box the text never outgrows it, so it
 * never pushes and the stack's own shrink_to_fit never touches it.
 *
 * Outside a stack a fixed height only matters for shrinking (the stitcher
 * ignores it otherwise), so the editor offers it as part of one choice,
 * "Shrink text": never, to fit its box (which fixes the height), or, for a
 * text in a stack, when the stack runs out of room (the stack's shrink_to_fit
 * with this member's shrink_with_stack and min_font_size; see helpers/stacks).
 * In a stack a fixed height without shrinking to it is the height reserved
 * before the text pushes, offered there as "Reserve height".
 *
 * A var without a fixed box carries none of the keys. The validation mirrors
 * the server's rules and messages so problems surface in the editor rather
 * than on save.
 */
import {
  STACK_MIN_FONT_SIZE,
  metadataSaveErrors,
  memberMinFontSize,
  shrinksWithStack,
  stackIdOf,
} from './stacks';

/** The same floor as stacks: 6 pt at 300 dpi. */
export const BOX_MIN_FONT_SIZE = STACK_MIN_FONT_SIZE;

export const TEXT_BOX_KEYS = [
  'fixed_height',
  'shrink_to_box',
  'box_min_font_size',
];

export const TEXT_BOX_DEFAULTS = Object.freeze({
  fixed_height: false,
  shrink_to_box: false,
  box_min_font_size: BOX_MIN_FONT_SIZE,
});

const isNumber = value => typeof value === 'number' && Number.isFinite(value);
const present = value => value !== undefined && value !== null;

/** Whether the text's box keeps its height rather than fitting its text. */
export const isFixedHeight = object => object?.metadata?.fixed_height === true;

/** Whether the stitcher shrinks the text to fit its fixed box. */
export const shrinksToBox = object =>
  isFixedHeight(object) && object.metadata.shrink_to_box === true;

/**
 * How a text shrinks when it renders too long: "box" (to fit its own fixed
 * box), "stack" (when its stack runs out of room) or "off".
 */
export const shrinkModeOf = object => {
  if (shrinksToBox(object)) return 'box';
  if (stackIdOf(object) !== null && shrinksWithStack(object)) return 'stack';
  return 'off';
};

/** The floor of the way the text shrinks (the default when it does not). */
export const shrinkMinFontSize = object => {
  const mode = shrinkModeOf(object);
  if (mode === 'box') return textBoxSettings(object.metadata).box_min_font_size;
  if (mode === 'stack') return memberMinFontSize(object);
  return BOX_MIN_FONT_SIZE;
};

/**
 * What a text's box does at render:
 *
 *   "shrink"  - shrinks its text to fit the box
 *   "reserve" - a fixed height in a stack: text taller than the box pushes
 *               the members below by the difference
 *   "fixed"   - a fixed height outside a stack, which does nothing at render
 *               (left over from a stack); the text runs past it as usual
 *   null      - the box fits its text
 */
export const boxRole = object => {
  if (!isFixedHeight(object)) return null;
  if (shrinksToBox(object)) return 'shrink';
  return stackIdOf(object) !== null ? 'reserve' : 'fixed';
};

/**
 * The settings after turning shrink to box on or off. Shrinking needs a box,
 * so turning it on fixes the height where the box is now. Turning it off lets
 * the box fit its text again, except in a stack, where the fixed height is
 * kept as the reserved height.
 */
export const withShrinkToBox = (settings, on, inStack) => ({
  ...settings,
  fixed_height: on || (inStack && settings.fixed_height === true),
  shrink_to_box: on,
});

/**
 * The settings after turning a fixed height without shrinking on or off: the
 * reserved height of a text in a stack.
 */
export const withReservedHeight = (settings, on) => ({
  ...settings,
  fixed_height: on,
  shrink_to_box: false,
});

/**
 * Checks the box keys of one var's metadata the way the server does. Returns
 * an object of messages keyed by the offending key; empty when valid. A null
 * value counts as absent, as on the server.
 */
export const validateTextBox = metadata => {
  const errors = {};
  const { fixed_height, shrink_to_box, box_min_font_size } = metadata || {};

  if (present(fixed_height) && typeof fixed_height !== 'boolean') {
    errors.fixed_height = 'fixed_height must be a boolean';
  }
  if (present(shrink_to_box) && typeof shrink_to_box !== 'boolean') {
    errors.shrink_to_box = 'shrink_to_box must be a boolean';
  } else if (shrink_to_box === true && fixed_height !== true) {
    errors.shrink_to_box = 'shrink_to_box requires fixed_height';
  }
  if (
    present(box_min_font_size) &&
    !(isNumber(box_min_font_size) && box_min_font_size >= BOX_MIN_FONT_SIZE)
  ) {
    errors.box_min_font_size = `box_min_font_size must be a number of at least ${BOX_MIN_FONT_SIZE}`;
  }

  return errors;
};

/** The box settings in the metadata, defaults filled in. */
export const textBoxSettings = metadata => {
  const settings = { ...TEXT_BOX_DEFAULTS };
  for (const key of TEXT_BOX_KEYS) {
    if (present(metadata?.[key])) settings[key] = metadata[key];
  }
  return settings;
};

/**
 * A copy of the metadata with the box settings written. Only what is in use
 * is stored: nothing at all without a fixed height, and the floor only while
 * shrinking, so an ordinary text's metadata (and the stitcher config built
 * from it) is unchanged. Never mutates.
 */
export const withTextBox = (metadata, settings) => {
  const rest = { ...(metadata || {}) };
  TEXT_BOX_KEYS.forEach(key => delete rest[key]);
  if (settings.fixed_height !== true) return rest;
  if (settings.shrink_to_box !== true) return { ...rest, fixed_height: true };
  return {
    ...rest,
    fixed_height: true,
    shrink_to_box: true,
    box_min_font_size: settings.box_min_font_size,
  };
};

/**
 * Turns the values a settings form yields into box settings, reporting what
 * cannot be parsed. A blank floor falls back to the default.
 */
export const parseTextBoxForm = form => {
  const settings = {
    ...TEXT_BOX_DEFAULTS,
    fixed_height: Boolean(form.fixed_height),
    shrink_to_box: Boolean(form.fixed_height) && Boolean(form.shrink_to_box),
  };
  const errors = {};

  const raw = form.box_min_font_size;
  if (present(raw) && String(raw).trim() !== '') {
    const value = Number(raw);
    if (Number.isFinite(value)) settings.box_min_font_size = value;
    else errors.box_min_font_size = 'box_min_font_size must be a number';
  }

  return {
    settings,
    errors: { ...validateTextBox(settings), ...errors },
  };
};

/**
 * How far the text on the canvas reaches past the bottom of its fixed box, in
 * template px (0 when it fits or the box is not fixed). `textHeight` is the
 * height the text measured at its last layout (see objects/fixedHeight).
 */
export const boxOverflow = object => {
  if (!isFixedHeight(object) || !isNumber(object.textHeight)) return 0;
  const over = (object.textHeight - object.height) * (object.scaleY || 1);
  return over > 0 ? over : 0;
};

/** The fixed-box messages in a failed template save (see stackSaveErrors). */
export const textBoxSaveErrors = error =>
  metadataSaveErrors(error).filter(message =>
    /\b(fixed_height|shrink_to_box|box_min_font_size)\b/.test(message)
  );
